import { useMemo, useState } from "react";

import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import postalMixIcon from "../../../assets/icons/postal-mix.png";
import { useGeneratePostalCodesMutation } from "../cargoApiSlice";

export const PostalCodesMixer = ({
	watch,
	setValue,
	counter,
	setCounter,
	setAddressBeforeMix,
}) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();

	const [generatePostalCodesMutation, { isLoading }] =
		useGeneratePostalCodesMutation();
	const [loadingRadius, setLoadingRadius] = useState(30);
	const [unloadingRadius, setUnloadingRadius] = useState(30);

	const privateNote = watch("privateNote");

	const loadingData = {
		country: watch("loadingAddressCountry"),
		address: watch("loadingAddress"),
		customPostal: watch("loadingAddressCustomPostalCode"),
		customCity: watch("loadingAddressCustomCity"),
		customCountry: watch("loadingAddressCustomCountry"),
	};

	const unloadingData = {
		country: watch("unloadingAddressCountry"),
		address: watch("unloadingAddress"),
		customPostal: watch("unloadingAddressCustomPostalCode"),
		customCity: watch("unloadingAddressCustomCity"),
		customCountry: watch("unloadingAddressCustomCountry"),
	};

	const isLoadingAddress =
		loadingData?.address &&
		loadingData.address?.address?.city &&
		loadingData.address?.address?.postalCode
			? true
			: false;
	const isUnloadingAddress =
		unloadingData?.address &&
		unloadingData.address?.address?.city &&
		unloadingData.address?.address?.postalCode
			? true
			: false;

	const isCustomLoadingAddress =
		loadingData.customCity &&
		loadingData.customPostal &&
		loadingData.customCountry
			? true
			: false;
	const isCustomUnloadingAddress =
		unloadingData.customCity &&
		unloadingData.customPostal &&
		unloadingData.customCountry
			? true
			: false;

	const shouldDisable = useMemo(
		() =>
			(!isLoadingAddress && !isCustomLoadingAddress) ||
			(!isUnloadingAddress && !isCustomUnloadingAddress) ||
			counter !== 0,
		[
			counter,
			isLoadingAddress,
			isCustomLoadingAddress,
			isUnloadingAddress,
			isCustomUnloadingAddress,
		]
	);

	const handleMix = () => {
		if (shouldDisable) return;
		generatePostalCodesMutation([
			{
				country: loadingData.country,
				city: loadingData?.address?.address?.city
					? loadingData.address.address.city
					: loadingData.customCity,
				postcode: loadingData?.address?.address?.postalCode
					? loadingData.address.address.postalCode
					: loadingData.customPostal,
				radius: loadingRadius * 1000,
			},
			{
				country: unloadingData.country,
				city: unloadingData?.address?.address?.city
					? unloadingData.address.address.city
					: unloadingData.customCity,
				postcode: unloadingData?.address?.address?.postalCode
					? unloadingData.address.address.postalCode
					: unloadingData.customPostal,
				radius: unloadingRadius * 1000,
			},
		])
			.unwrap()
			.then((payload) => {
				const [newLoading, newUnloading] = payload;
				if (newLoading.success && newUnloading.success) {
					const oldLoadingAddress = loadingData?.address?.title
						? loadingData.address.title
						: `${loadingData.country}, ${loadingData.customPostal}, ${loadingData.customCity}`;
					const oldUnloadingAddress = unloadingData?.address?.title
						? unloadingData.address.title
						: `${unloadingData.country}, ${unloadingData.customPostal}, ${unloadingData.customCity}`;

					setAddressBeforeMix({
						loading: {
							country: loadingData.country,
							city: loadingData?.address?.address?.city
								? loadingData.address.address.city
								: loadingData.customCity,
							postcode: loadingData?.address?.address?.postalCode
								? loadingData.address.address.postalCode
								: loadingData.customPostal,
						},
						unloading: {
							country: unloadingData.country,
							city: unloadingData?.address?.address?.city
								? unloadingData.address.address.city
								: unloadingData.customCity,
							postcode: unloadingData?.address?.address?.postalCode
								? unloadingData.address.address.postalCode
								: unloadingData.customPostal,
						},
					});

					setValue("loadingAddress", newLoading.data);
					setValue("unloadingAddress", newUnloading.data);

					// build privateNote
					const privateNoteParts = [];
					// put old addresses at the beginning of private note
					privateNoteParts.push(
						`[${t(
							"original_address_label"
						)}: ${oldLoadingAddress} -> ${oldUnloadingAddress}]`
					);
					// append existing private note if exists
					if (privateNote) privateNoteParts.push(privateNote);
					// combine parts into new private note
					const newPrivateNote = privateNoteParts.join("\n");

					setValue("privateNote", newPrivateNote);
					setCounter((prev) => prev + 1);
				}
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([_key, message]) => {
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	return (
		<>
			<Tooltip
				title={
					<Typography variant="p" sx={{ fontSize: "9pt" }} component="span">
						{shouldDisable
							? t("postal_code_mixer_tooltip_disabled")
							: t("postal_code_mixer_tooltip_enabled")}
					</Typography>
				}
				placement="left"
			>
				<Box
					onClick={handleMix}
					sx={{
						maxWidth: "25px",
						maxHeight: "25px",
						width: "25px",
						height: "25px",
						cursor: shouldDisable ? "not-allowed" : "pointer",
						opacity: shouldDisable ? 0.5 : 1,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						border: "1px solid #e97132",
						borderRadius: "7px",
						padding: "2px",
					}}
				>
					{isLoading && <CircularProgress size="14px" />}
					{!isLoading && (
						<img
							src={postalMixIcon}
							style={{ height: "100%", width: "100%", objectFit: "contain" }}
							alt="mix postal codes"
						/>
					)}
				</Box>
			</Tooltip>
			<Box sx={{ display: "flex", gap: "2px" }}>
				{/* Loading Radius Buttons */}
				<Box
					sx={{
						flex: "0 0 50%",
						display: "flex",
						flexDirection: "column",
						gap: "4px",
					}}
				>
					<RadiusSelector
						distance={45}
						setLoadingRadius={setLoadingRadius}
						isSelected={loadingRadius === 45}
						disabled={shouldDisable}
					/>
					<RadiusSelector
						distance={60}
						setLoadingRadius={setLoadingRadius}
						isSelected={loadingRadius === 60}
						disabled={shouldDisable}
					/>
					<RadiusSelector
						distance={75}
						setLoadingRadius={setLoadingRadius}
						isSelected={loadingRadius === 75}
						disabled={shouldDisable}
					/>
				</Box>
				{/* Unloading Radius Buttons */}
				<Box
					sx={{
						flex: "0 0 50%",
						display: "flex",
						flexDirection: "column",
						gap: "4px",
					}}
				>
					<RadiusSelector
						distance={45}
						setLoadingRadius={setUnloadingRadius}
						isSelected={unloadingRadius === 45}
						disabled={shouldDisable}
					/>
					<RadiusSelector
						distance={60}
						setLoadingRadius={setUnloadingRadius}
						isSelected={unloadingRadius === 60}
						disabled={shouldDisable}
					/>
					<RadiusSelector
						distance={75}
						setLoadingRadius={setUnloadingRadius}
						isSelected={unloadingRadius === 75}
						disabled={shouldDisable}
					/>
				</Box>
			</Box>
		</>
	);
};

const RadiusSelector = ({
	distance,
	setLoadingRadius,
	isSelected,
	disabled,
}) => {
	const { t } = useTranslation();

	return (
		<Tooltip
			title={
				<Typography variant="p" sx={{ fontSize: "9pt" }} component="span">
					{t("postal_code_mixer_radius_selector")}
				</Typography>
			}
			placement="left"
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					border: "none",
					cursor: disabled ? "not-allowed" : "pointer",
					background: isSelected ? "#a0cbeb" : "#d6eeff",
					borderRadius: "100%",
					height: "18px",
					width: "18px",
					fontSize: "11px",
				}}
				type="button"
				component="button"
				onClick={() =>
					setLoadingRadius((prev) => (prev === distance ? 30 : distance))
				}
				disabled={disabled}
			>
				{distance}
			</Box>
		</Tooltip>
	);
};
